import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Collapse,
    Divider,
    Theme,
    Typography
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { Skeleton } from '@material-ui/lab';
import { makeStyles } from '@material-ui/styles';
import { IItemReadResourceV10 } from 'components/basket/model/Basket';
import { IOrderTotal } from 'components/order/model/Order';
import { getFadeDownGradient } from 'lib/helpers';
import { PayOrderItem } from './PayOrderItem';
import { useAuth } from 'lib/useAuth';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

interface PayItemsProps {
    loading?: boolean;
    items?: IItemReadResourceV10[];
    total?: IOrderTotal;
    keepOpen?: boolean;
    onItemClick?: (id: number) => void;
    claimedItems?: number[];
    hideOtherItems?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
    itemsBox: {
        position: 'relative'
    },
    itemsButton: {
        textTransform: 'unset',
        backgroundColor: theme.palette.background.default,
        paddingTop: theme.spacing(0.25),
        paddingBottom: theme.spacing(1.25),
        borderRadius: 0,
        '&.MuiButton-root:hover': {
            backgroundColor: theme.palette.background.default
        }
    },
    itemsButtonWrapper: {
        position: 'absolute',
        bottom: 1,
        width: `calc(100% - ${theme.spacing(4)}px)`
    },
    itemsFade: {
        width: '100%',
        height: '34px',
        background: getFadeDownGradient(theme.palette.background.default, [0, 0.6, 1])
    },
    accordionRoot: {
        padding: 0,
        '&.Mui-expanded': {
            marginTop: 0
        }
    },
    accordionSummary: {
        padding: 0,
        margin: 0,
        '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: theme.spacing(2, 0),
            minHeight: '32px'
        },
        '& .MuiAccordionSummary-expandIcon.Mui-expanded': {
            padding: theme.spacing(0, 2)
        }
    },
    accordionDetails: {
        display: 'block'
    }
}));

const SKELETON_ITEMS_ARRAY = [0, 1, 2, 3];

export const PayItems: React.FC<PayItemsProps> = ({
    loading,
    items,
    keepOpen,
    onItemClick,
    hideOtherItems = false,
    claimedItems
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const { user } = useAuth();

    const handleClick = React.useCallback(() => {
        setOpen(isOpen => !isOpen);
    }, []);

    const filteredItems = React.useMemo(() => {
        if (Array.isArray(items)) {
            return items.filter(item => !item.deleted);
        }
        return [];
    }, [items]);

    const itemSplittingItems = React.useMemo(() => {
        const resultArray: IItemReadResourceV10[] = [];
        filteredItems.forEach(item => {
            if (item.quantity > 1) {
                const isClaimedBy = item.isClaimedBy ? [...item.isClaimedBy] : [];
                Array(item.quantity)
                    .fill(0)
                    .forEach((_, index) => {
                        const isClaimedBySingular = isClaimedBy.shift();
                        resultArray.push({
                            ...item,
                            quantity: 1,
                            _uiKey: `${item.id}+${index}`,
                            isClaimedBy: isClaimedBySingular ? [isClaimedBySingular] : undefined
                        });
                    });
            } else {
                resultArray.push(item);
            }
        });
        return resultArray;
    }, [filteredItems]);

    const [myItems, otherItems] = React.useMemo(() => {
        if (!user) {
            return [[], []];
        }
        const copyOfClaimedItemIds = [...(claimedItems || [])];
        const myItemsArray: IItemReadResourceV10[] = [];
        const otherItemsArray: IItemReadResourceV10[] = [];
        itemSplittingItems.forEach(item => {
            if (item.isClaimedBy?.includes(user._id) && copyOfClaimedItemIds.includes(item.id)) {
                copyOfClaimedItemIds.splice(copyOfClaimedItemIds.indexOf(item.id), 1);
                myItemsArray.push(item);
            } else {
                otherItemsArray.push(item);
            }
        });
        return [myItemsArray, otherItemsArray];
    }, [claimedItems, itemSplittingItems, user]);

    const renderItem = React.useCallback(
        (item: IItemReadResourceV10) => (
            <PayOrderItem
                key={item._uiKey ?? item.id}
                item={item}
                allItems={filteredItems}
                onClick={onItemClick}
            />
        ),
        [filteredItems, onItemClick]
    );
    const renderSecondaryItem = React.useCallback(
        (item: IItemReadResourceV10) => (
            <PayOrderItem
                key={item._uiKey ?? item.id}
                item={item}
                allItems={filteredItems}
                onClick={onItemClick}
                secondary
            />
        ),
        [filteredItems, onItemClick]
    );
    const renderSkeleton = React.useCallback(
        (item: number) => (
            <Box display="flex" justifyContent="space-between" key={`skeleton-${item}`} py={0.25}>
                <Box display="flex">
                    <Box paddingRight={1}>
                        <Skeleton variant="text" width={18} />
                    </Box>
                    <Skeleton variant="text" width={120} />
                </Box>
                <Skeleton variant="text" width={36} />
            </Box>
        ),
        []
    );
    const showItemSplitting = React.useMemo(
        () => claimedItems && claimedItems.length > 0 && myItems && myItems.length > 0,
        [claimedItems, myItems]
    );
    const collapse = React.useMemo(() => {
        if (!filteredItems.length || keepOpen || showItemSplitting) {
            return false;
        }
        const count = filteredItems.reduce((acc, item) => {
            if (item.modifiers) {
                item.modifiers.forEach(modifier => {
                    modifier.options.forEach(_ => {
                        acc += 1;
                    });
                });
            }
            acc += 1;
            return acc;
        }, 0);
        return count > 4;
    }, [filteredItems, keepOpen, showItemSplitting]);
    if (loading) {
        return (
            <Box px={2} className={classes.itemsBox}>
                <Box mb={2.5}>{SKELETON_ITEMS_ARRAY.map(renderSkeleton)}</Box>
                <Divider />
            </Box>
        );
    }
    if (!!filteredItems.length) {
        return (
            <Box px={2} className={classes.itemsBox}>
                {collapse && (
                    <Collapse in={open} collapsedSize={112}>
                        <Box mb={4}>{filteredItems.map(renderItem)} </Box>
                    </Collapse>
                )}
                {!collapse && !showItemSplitting && (
                    <Box mb={keepOpen ? 1 : 2.5}>{filteredItems.map(renderItem)}</Box>
                )}
                {!collapse && showItemSplitting && (
                    <Box mb={0}>
                        {myItems.length > 0 && (
                            <Box pt={1}>
                                <Typography variant="h6">{t('PAT_QUICKPAY_MY_ITEMS')}</Typography>
                                {myItems.map(renderItem)}
                            </Box>
                        )}
                        {otherItems.length > 0 && !hideOtherItems && (
                            <Accordion elevation={0} className={classes.accordionRoot}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="panel1a-content"
                                    id="panel1a-header"
                                    className={classes.accordionSummary}
                                >
                                    <Typography variant="h6">{t('PAT_QUICKPAY_OTHER_ITEMS')}</Typography>
                                </AccordionSummary>
                                <AccordionDetails className={classes.accordionDetails}>
                                    <Typography>{otherItems.map(renderSecondaryItem)}</Typography>
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </Box>
                )}
                {collapse && (
                    <Box className={classes.itemsButtonWrapper}>
                        {!open && <div className={classes.itemsFade} />}
                        <Button
                            fullWidth
                            onClick={handleClick}
                            className={classes.itemsButton}
                            disableTouchRipple
                        >
                            {open ? t('PAT_QUICKPAY_SHOW_LESS_ITEMS') : t('PAT_QUICKPAY_SHOW_MORE_ITEMS')}
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </Button>
                    </Box>
                )}
                <Divider />
            </Box>
        );
    }
    return null;
};
